import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SeverityBadge, StatusBadge, TypeBadge } from "./RiskStatusBadge";
import type { Risk } from "../types";
import { formatDistanceToNow } from "date-fns";

interface RisksTableProps {
  risks: Risk[];
  orderBy: string;
  order: "asc" | "desc";
  onSort: (property: string) => void;
}

const columns = [
  { id: "id", label: "ID", width: 100 },
  { id: "severity", label: "Severity", width: 100 },
  { id: "title", label: "Alert", width: "auto" },
  { id: "type", label: "Type", width: 120 },
  { id: "status", label: "Status", width: 140 },
  { id: "location", label: "Location", width: 180 },
  { id: "timestamp", label: "Time", width: 140 },
  { id: "actions", label: "", width: 60 },
];

export const RisksTable = ({ risks, orderBy, order, onSort }: RisksTableProps) => {
  const navigate = useNavigate();

  const handleRowClick = (riskId: string) => {
    navigate(`/risks/${riskId}`);
  };

  if (risks.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No alerts found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your filters to see more results
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Card} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} sx={{ width: column.width }}>
                {column.id !== "actions" ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => onSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : null}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {risks.map((risk) => (
            <TableRow
              key={risk.id}
              hover
              onClick={() => handleRowClick(risk.id)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {risk.id}
                </Typography>
              </TableCell>
              <TableCell>
                <SeverityBadge severity={risk.severity} />
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
                    {risk.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 350 }} display="block">
                    {risk.description}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <TypeBadge type={risk.type} />
              </TableCell>
              <TableCell>
                <StatusBadge status={risk.status} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                    {risk.location}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(risk.timestamp), { addSuffix: true })}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(risk.id);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
